type UserData = {
    userId: string | undefined;
    company: string | undefined;
    countryCode: string | undefined;
    department: string | undefined;
    jobRole: string | undefined;
    region: string | undefined;
    isExternal: boolean;
};

type WorkspaceData = {
    market?: string;
    industry?: string;
    brand?: string;
    client?: string;
    region?: string;
    custom?: string;
};

type ApplicationData = {
    applicationId: string | undefined;
    applicationInstanceId: string | undefined;
    productType: string;
    nocodeApptype?: string;
    productName: string;
    productCategory: string;
    productSubcategory: string;
};

type ContextData = {
    contextType: "os" | "standalone";
    project?: string;
    tenantName?: string;
};

type WindowWithDataLayer = Record<string, unknown> & Window;

/**
 * Module-scoped logger that groups all log() calls under "GA4Tracker"
 * while keeping warn() and error() at the top level.
 */
const logger = (() => {
    const GROUP_NAME = "GA4Tracker";

    return {
        log(...args: unknown[]) {
            console.group(GROUP_NAME);
            // console.groupCollapsed(GROUP_NAME);
            console.log(...args);
            console.groupEnd();
        },
        warn(...args: unknown[]) {
            console.warn(`[${GROUP_NAME}]`, ...args);
        },
        error(...args: unknown[]) {
            console.error(`[${GROUP_NAME}]`, ...args);
        },
    };
})();

/**
 * Simple fallback hash for non-secure contexts (localhost dev).
 * This is NOT cryptographically secure - only for local testing.
 * This is fine, especially since we're not actually sending events in dev envs
 */
function devFallbackHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash; // Convert to 32bit integer
    }

    // Pad to look like a SHA-256 hash (64 hex chars)
    const base = Math.abs(hash).toString(16).padStart(8, "0");
    return `dev_${base.repeat(8).slice(0, 60)}`;
}

async function hashEmail(email: string): Promise<string> {
    const normalizedEmail = email.trim().toLowerCase();

    // crypto.subtle is only available in secure contexts (HTTPS or localhost with some browsers)
    if (typeof crypto !== "undefined" && crypto.subtle) {
        const msgBuffer = new TextEncoder().encode(normalizedEmail);
        const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);

        return Array.from(new Uint8Array(hashBuffer))
            .map((b) => b.toString(16).padStart(2, "0"))
            .join("");
    }

    // Fallback for non-secure contexts (HTTP localhost)
    logger.warn("crypto.subtle unavailable (non-secure context). Using dev fallback hash.");
    return devFallbackHash(normalizedEmail);
}

const INSTANCE_KEY = "__GA4Tracker_instance__";

export class GA4Tracker {
    private static _instance: GA4Tracker | null = null;

    private static get instance(): GA4Tracker | null {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (import.meta?.env?.DEV) {
            return (window as unknown as Record<string, unknown>)[
                INSTANCE_KEY
            ] as GA4Tracker | null;
        }

        return this._instance;
    }

    private static set instance(value: GA4Tracker | null) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (import.meta?.env?.DEV) {
            (window as unknown as Record<string, unknown>)[INSTANCE_KEY] = value;
        } else {
            this._instance = value;
        }
    }

    private dataLayerName: string;
    private userData: UserData | null = null;
    private wppWorkspaceData: WorkspaceData = {};
    private applicationData: ApplicationData | null = null;
    private contextData: ContextData | null = null;
    private preventTracking: boolean;

    private _isInitialized = false;

    get isInitialized(): boolean {
        return this._isInitialized;
    }

    static createInstance(
        config: { dataLayerName?: string; preventTracking?: boolean } = {},
    ): GA4Tracker {
        if (GA4Tracker.instance) {
            console.error(
                "Failed to create GA4Tracker instance: GA4Tracker instance already exists",
            );

            return GA4Tracker.instance;
        }

        GA4Tracker.instance = new GA4Tracker(config.dataLayerName, config.preventTracking);

        return GA4Tracker.instance;
    }

    static getInstance(): GA4Tracker {
        if (!GA4Tracker.instance) {
            throw new Error("GA4Tracker not initialized. Call createInstance() first.");
        }

        return GA4Tracker.instance;
    }

    private constructor(dataLayerName = "wppopen_dataLayer", preventTracking = false) {
        this.dataLayerName = dataLayerName;
        this.preventTracking = preventTracking;

        this.isInitializing = false;

        const windowWithDataLayer = window as unknown as WindowWithDataLayer;
        windowWithDataLayer[this.dataLayerName] = windowWithDataLayer[this.dataLayerName] ?? [];
    }

    isInitializing: boolean;

    private push(eventData: Record<string, unknown>) {
        if (this.preventTracking) {
            logger.log(
                "Tracking prevented - running in Open OS iframe or feature branch. Event data:",
                eventData,
            );

            return;
        }

        const windowWithDataLayer = window as unknown as WindowWithDataLayer;
        const dataLayer = windowWithDataLayer[this.dataLayerName] as unknown[];

        logger.log("Trying to push event to data layer:", eventData);

        if (Array.isArray(dataLayer)) {
            dataLayer.push(eventData);
            logger.log("Event pushed to data layer successfully");
        } else {
            logger.error("Data layer is not an array:", this.dataLayerName);
        }
    }

    async setUserData(userData: Omit<UserData, "userId"> & { email: string }) {
        this.userData = {
            userId: await hashEmail(userData.email),
            company: userData.company,
            countryCode: userData.countryCode,
            department: userData.department,
            jobRole: userData.jobRole,
            region: userData.region,
            isExternal: userData.isExternal,
        };
    }

    setWppWorkspaceData(workspaceData: WorkspaceData) {
        this.wppWorkspaceData = workspaceData;
    }

    setApplicationData(applicationData: ApplicationData) {
        this.applicationData = applicationData;
    }

    setContextData(contextData: ContextData) {
        this.contextData = contextData;
    }

    setInitialized() {
        this._isInitialized = true;
    }

    private getCommonProperties() {
        return {
            user_id: this.userData?.userId,
            user_company: this.userData?.company,
            user_country_code: this.userData?.countryCode,
            user_department: this.userData?.department,
            user_job_role: this.userData?.jobRole,
            user_region: this.userData?.region,
            user_is_external: this.userData?.isExternal ?? false,
            workspace_market: this.wppWorkspaceData.market,
            workspace_industry: this.wppWorkspaceData.industry,
            workspace_brand: this.wppWorkspaceData.brand,
            workspace_client: this.wppWorkspaceData.client,
            workspace_region: this.wppWorkspaceData.region,
            workspace_custom: this.wppWorkspaceData.custom,
            context_type: this.contextData?.contextType ?? "standalone",
            project: this.contextData?.project,
            tenant_name: this.contextData?.tenantName,
            ...(this.applicationData
                ? {
                      application_id: this.applicationData.applicationId,
                      application_instance_id: this.applicationData.applicationInstanceId,
                      product_type: this.applicationData.productType,
                      nocode_apptype: this.applicationData.nocodeApptype,
                      product_name: this.applicationData.productName,
                      product_category: this.applicationData.productCategory,
                      product_subcategory: this.applicationData.productSubcategory,
                  }
                : {}),
        };
    }

    trackUserLogin() {
        if (!this.userData) {
            logger.error("User data must be set before tracking user login");
            return;
        }

        this.push({
            event: "user_login",
            ...this.getCommonProperties(),
        });
    }

    trackAppLaunch() {
        if (!this.applicationData) {
            logger.error("Application data must be set before tracking app launch");
            return;
        }

        this.push({
            event: "app_launch",
            ...this.getCommonProperties(),
        });
    }

    trackPageView() {
        this.push({
            event: "page_view",
            ...this.getCommonProperties(),
        });
    }

    trackVirtualPageView(pageUrl: string, pageTitle: string, previousUrl: string) {
        this.push({
            event: "virtual_page_view",
            ...this.getCommonProperties(),
            pageUrl,
            pageTitle,
            previousUrl,
        });
    }

    trackClick(
        clickObject: "Image" | "Button" | "Link" | "Icon",
        clickText: string,
        clickPagePath: string = window.location.pathname,
        clickTargetPath?: string,
    ) {
        this.push({
            event: "click",
            ...this.getCommonProperties(),
            click_object: clickObject,
            click_text: clickText,
            click_page_path: clickPagePath,
            click_target_path: clickTargetPath ?? undefined,
        });
    }

    trackFeatureEvent({
        featureEvent,
        featureCategory,
        featureContext,
        freeformValues,
        clientId,
    }: {
        featureEvent: string;
        featureCategory: string;
        featureContext: string;
        freeformValues?: (string | number)[];
        clientId?: string;
    }) {
        if (!this.contextData || (!this.userData && !this.applicationData)) {
            logger.warn("Tracker not initialized; missing data");
            return;
        }

        const eventData: Record<string, string | number | boolean | undefined> = {
            event: featureEvent,
            ...this.getCommonProperties(),
            feature_category: featureCategory,
            feature_context: featureContext,
            client_id: clientId,
        };

        freeformValues?.forEach((value, index) => {
            if (index > 10) {
                return;
            }

            eventData[`feature_freeform_${index + 1}`] = value;
        });

        this.push(eventData);
    }
}
